import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import {
  cameraErrorName,
  classifyCameraError,
  forgetLastSelectedCamera,
  nextCameraRecovery,
  reloadPage,
  startCameraWatchdog,
  warmUpCamera,
} from '@/Pages/Selfie/helpers/cameraAccess'
import type { CameraRestartInfo, CameraRestartReason } from '@/Pages/Selfie/helpers/cameraAccess'
import { findVideoElement } from '@/Pages/Selfie/helpers/captureVideoFrame'
import { installCameraStreamTracker } from '@/Pages/Selfie/helpers/cameraStreamTracker'
import type { CameraStreamTracker } from '@/Pages/Selfie/helpers/cameraStreamTracker'

/** Estados do detector do Amplify que querem dizer "a câmera não abriu". */
const CAMERA_START_ERROR_STATES = new Set([
  'CAMERA_ACCESS_ERROR',
  'CAMERA_FRAMERATE_ERROR',
  'DEFAULT_CAMERA_NOT_FOUND_ERROR',
  'RUNTIME_ERROR',
])

function detectorErrorState(error: unknown): string | null {
  if (error !== null && typeof error === 'object' && 'state' in error && typeof error.state === 'string') {
    return error.state
  }
  return null
}

/**
 * Erro no formato do detector (`{ state, error }`), que `resolveLivenessError`
 * traduz para `device_error` e `livenessErrorDetails` leva ao log do painel.
 */
function toCameraFailure(error: unknown): unknown {
  if (detectorErrorState(error) !== null) return error
  if (error instanceof Error) return { state: 'CAMERA_ACCESS_ERROR', error }
  const timeout = new Error('Camera did not deliver a frame in time')
  timeout.name = 'CameraStartTimeout'
  return { state: 'CAMERA_ACCESS_ERROR', error: timeout }
}

type Options = {
  enabled: boolean
  /** Sessão AWS Face Liveness — sessão nova recomeça a câmera do zero. */
  sessionId: string
  /** O detector está na tela (credencial pronta) — só então o vigia conta. */
  detectorRendered: boolean
  cameraReady: boolean
  containerRef: RefObject<HTMLElement>
  onRestart: (info: CameraRestartInfo) => void
  onFatalError: (error: unknown) => void
}

type Result = {
  /** A câmera foi aquecida e liberada: o detector pode montar. */
  canMountDetector: boolean
  /** `key` do detector — muda a cada reabertura para ele montar limpo. */
  detectorKey: string
  /** A câmera está sendo reaberta depois de travar ou falhar. */
  isRecovering: boolean
  /**
   * Fica com o erro do detector quando é "a câmera não abriu" e vale reabrir
   * (devolve true). Permissão negada ou erro depois da câmera no ar seguem o
   * caminho normal da tela de ajuda (devolve false).
   */
  handleDetectorError: (error: unknown) => boolean
}

/**
 * Deixa a câmera do Face Liveness abrindo de primeira. Aquece a câmera antes do
 * detector (o prompt de permissão acontece aqui, com retentativa), vigia o
 * vídeo até o primeiro quadro e, se travar ou o detector falhar ao abrir a
 * câmera, reabre do zero — fechando inclusive o stream que o Amplify deixa
 * vazado. Esgotadas as reaberturas, recarrega a página uma vez; depois disso,
 * entrega o erro para a tela de ajuda.
 *
 * Reabrir reusa a MESMA sessão AWS de propósito: antes do primeiro quadro o
 * detector ainda não abriu o streaming, então a sessão não foi consumida.
 */
export function useLivenessCameraGuard({
  enabled,
  sessionId,
  detectorRendered,
  cameraReady,
  containerRef,
  onRestart,
  onFatalError,
}: Options): Result {
  const [camera, setCamera] = useState({ mountId: 0, canMount: false, recovering: false })
  const trackerRef = useRef<CameraStreamTracker | null>(null)
  const restartRef = useRef<(reason: CameraRestartReason, error?: unknown) => void>(() => undefined)
  const cameraReadyRef = useRef(cameraReady)
  const onRestartRef = useRef(onRestart)
  const onFatalErrorRef = useRef(onFatalError)
  cameraReadyRef.current = cameraReady
  onRestartRef.current = onRestart
  onFatalErrorRef.current = onFatalError

  useEffect(() => {
    if (!enabled) return

    const tracker = installCameraStreamTracker()
    trackerRef.current = tracker
    let run = 0
    let restarts = 0
    let disposed = false

    const open = async (recovering: boolean) => {
      const current = ++run
      const isStale = () => disposed || current !== run
      setCamera((c) => ({ ...c, canMount: false, recovering }))
      tracker.stopAll()

      const result = await warmUpCamera(isStale)
      if (isStale()) return
      if (result.ok) {
        setCamera((c) => ({ mountId: c.mountId + 1, canMount: true, recovering }))
        return
      }
      if (result.kind === 'transient') {
        restart('warmup_failed', result.error)
        return
      }
      setCamera((c) => ({ ...c, recovering: false }))
      onFatalErrorRef.current(toCameraFailure(result.error))
    }

    const restart = (reason: CameraRestartReason, error?: unknown) => {
      if (disposed) return
      // O nome do navegador (NotReadableError…) diz mais que o `Error` genérico
      // que o Amplify monta; sem ele, o estado do detector (15 fps, acesso).
      const ownName = cameraErrorName(error)
      const errorName =
        (ownName !== null && ownName !== 'Error' ? ownName : null) ??
        tracker.lastErrorName() ??
        detectorErrorState(error)
      const next = nextCameraRecovery(restarts)

      if (next === 'restart') {
        restarts += 1
        onRestartRef.current({ reason, attempt: restarts, errorName, reloading: false })
        forgetLastSelectedCamera()
        void open(true)
        return
      }

      run += 1
      tracker.stopAll()
      if (next === 'reload') {
        setCamera((c) => ({ ...c, canMount: false, recovering: true }))
        onRestartRef.current({ reason, attempt: restarts + 1, errorName, reloading: true })
        forgetLastSelectedCamera()
        reloadPage()
        return
      }
      setCamera((c) => ({ ...c, canMount: false, recovering: false }))
      onFatalErrorRef.current(toCameraFailure(error))
    }

    restartRef.current = restart
    void open(false)

    return () => {
      disposed = true
      restartRef.current = () => undefined
      tracker.stopAll()
      tracker.uninstall()
      trackerRef.current = null
      setCamera((c) => ({ ...c, canMount: false, recovering: false }))
    }
  }, [enabled, sessionId])

  useEffect(() => {
    if (!enabled || !camera.canMount || !detectorRendered || cameraReady) return
    return startCameraWatchdog({
      getVideo: () => (containerRef.current ? findVideoElement(containerRef.current) : null),
      getPendingSince: () => trackerRef.current?.pendingSince() ?? null,
      onStall: (reason) => {
        restartRef.current(reason)
      },
    })
  }, [enabled, camera.canMount, camera.mountId, detectorRendered, cameraReady, containerRef])

  const handleDetectorError = useCallback(
    (error: unknown): boolean => {
      if (!enabled || cameraReadyRef.current) return false
      const state = detectorErrorState(error)
      if (state === null || !CAMERA_START_ERROR_STATES.has(state)) return false
      const lastName = trackerRef.current?.lastErrorName() ?? null
      if (lastName !== null && classifyCameraError({ name: lastName }) !== 'transient') return false
      restartRef.current('camera_error', error)
      return true
    },
    [enabled],
  )

  return {
    canMountDetector: enabled && camera.canMount,
    detectorKey: `${sessionId}:${camera.mountId}`,
    isRecovering: camera.recovering,
    handleDetectorError,
  }
}
